import type { ButtonStyle } from "@codway-dev/ui";
import {
  CollapsibleSectionCard,
  SectionHeader,
  Divider,
  ColorRow,
  NumericField,
} from "../../../components/shared/EditorShared";
import type { DownsellEditorState } from "../../../store/downsell-editor.mapping";
import { ButtonStyleFields } from "./DownsellEditorButtons";

// ─── Section 2: Personaliza el downsell ──────────────────────────────────────

export function DesignSection({
  values,
  onDesignChange,
}: {
  values: Pick<
    DownsellEditorState,
    | "titleTemplate"
    | "titleColor"
    | "titleSize"
    | "subtitleTemplate"
    | "subtitleColor"
    | "subtitleSize"
    | "discountBadgeText"
    | "discountBadgeTextColor"
    | "discountBadgeDiscountColor"
    | "discountBadgeSize"
    | "ctaAcceptStyle"
    | "ctaDeclineStyle"
  >;
  onDesignChange: <K extends keyof DownsellEditorState>(
    key: K,
    value: DownsellEditorState[K]
  ) => void;
}) {
  const {
    titleTemplate,
    titleColor,
    titleSize,
    subtitleTemplate,
    subtitleColor,
    subtitleSize,
    discountBadgeText,
    discountBadgeTextColor,
    discountBadgeDiscountColor,
    discountBadgeSize,
    ctaAcceptStyle,
    ctaDeclineStyle,
  } = values;

  return (
    <CollapsibleSectionCard
      header={<SectionHeader number={3} title="Personaliza el downsell" />}
    >
      <s-stack direction="block" gap="base">
        {/* Title */}
        <s-stack direction="block" gap="small-200">
          <s-text-field
            label="Título"
            value={titleTemplate}
            onInput={(e: { currentTarget: { value: string } }) =>
              onDesignChange("titleTemplate", e.currentTarget.value)
            }
          />
          <div className="grid grid-cols-2 gap-3">
            <ColorRow
              label="Color del titulo"
              value={titleColor}
              onChange={(v) => onDesignChange("titleColor", v)}
            />
            <NumericField
              label="Tamaño del texto"
              min={10}
              value={titleSize}
              onChange={(v) => onDesignChange("titleSize", v)}
            />
          </div>
        </s-stack>

        <Divider />

        {/* Subtitle */}
        <s-stack direction="block" gap="small-200">
          <s-text-field
            label="Subtitular"
            value={subtitleTemplate}
            onInput={(e: { currentTarget: { value: string } }) =>
              onDesignChange("subtitleTemplate", e.currentTarget.value)
            }
          />
          <div className="grid grid-cols-2 gap-3">
            <ColorRow
              label="Color del subtítulo"
              value={subtitleColor}
              onChange={(v) => onDesignChange("subtitleColor", v)}
            />
            <NumericField
              label="Tamaño del texto"
              min={10}
              value={subtitleSize}
              onChange={(v) => onDesignChange("subtitleSize", v)}
            />
          </div>
        </s-stack>

        <Divider />

        {/* Discount badge */}
        <s-stack direction="block" gap="small-200">
          <s-text type="strong">Placa de descuento</s-text>
          <s-text-field
            label="Texto"
            value={discountBadgeText}
            onInput={(e: { currentTarget: { value: string } }) =>
              onDesignChange("discountBadgeText", e.currentTarget.value)
            }
          />
          <div className="grid grid-cols-2 gap-3">
            <ColorRow
              label="Color de texto"
              value={discountBadgeTextColor}
              onChange={(v) => onDesignChange("discountBadgeTextColor", v)}
            />
            <ColorRow
              label="Color de descuento"
              value={discountBadgeDiscountColor}
              onChange={(v) => onDesignChange("discountBadgeDiscountColor", v)}
            />
          </div>
          <s-stack direction="block" gap="small-200">
            <s-text>Tamaño de la placa</s-text>
            <input
              type="range"
              min={20}
              max={120}
              value={discountBadgeSize}
              onChange={(e) =>
                onDesignChange("discountBadgeSize", Number(e.target.value))
              }
              className="w-full accent-indigo-600"
            />
          </s-stack>
        </s-stack>

        <Divider />

        {/* Accept button */}
        <s-stack direction="block" gap="small-200">
          <s-text type="strong">Botón de completar pedido</s-text>
          <s-text color="subdued">
            Use {"{discount}"} para insertar el valor del descuento dentro de
            este campo de texto.
          </s-text>
          <ButtonStyleFields
            style={ctaAcceptStyle}
            onChange={(partial) =>
              onDesignChange("ctaAcceptStyle", {
                ...ctaAcceptStyle,
                ...partial,
              } as ButtonStyle)
            }
          />
        </s-stack>

        <Divider />

        {/* Decline button */}
        <s-stack direction="block" gap="small-200">
          <s-text type="strong">Botón de no gracias</s-text>
          <ButtonStyleFields
            style={ctaDeclineStyle}
            onChange={(partial) =>
              onDesignChange("ctaDeclineStyle", {
                ...ctaDeclineStyle,
                ...partial,
              } as ButtonStyle)
            }
          />
        </s-stack>
      </s-stack>
    </CollapsibleSectionCard>
  );
}
